import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useContactChat } from "../context/ContactChatContext";
import { useScrolledPast } from "../hooks/useScrolledPast";
import { useFooterDockOffset } from "../hooks/useFooterDockOffset";
import { useLenis } from "./SmoothScroll";
import ContactForm from "./ContactForm";
import ContactServicePicker, { CONTACT_SERVICES } from "./ContactServicePicker";
import "./ContactChatPopup.css";

/** Same threshold as Back to Top — stack height includes BTT only when it can be visible. */
const SCROLL_FOR_BACK_TO_TOP = 400;

export default function ContactChatPopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const titleId = useId();
  const { contactOpen: open, openContactPopup, closeContactPopup, toggleContactPopup, contactDraft, setContactDraft } =
    useContactChat();
  const [mounted, setMounted] = useState(false);
  const lenis = useLenis();
  const backToTopLikelyVisible = useScrolledPast(SCROLL_FOR_BACK_TO_TOP);
  const footerOffset = useFooterDockOffset();

  const { fabBottom, panelBottom } = useMemo(() => {
    if (backToTopLikelyVisible) {
      return {
        fabBottom: "var(--contact-fab-stacked)",
        panelBottom: "calc(1rem + 3.5rem + 0.4rem)",
      };
    }
    return {
      fabBottom: "var(--contact-fab-base)",
      panelBottom: "calc(1rem + 3.5rem + 0.4rem)",
    };
  }, [backToTopLikelyVisible]);

  useEffect(() => setMounted(true), []);

  const isHome = location.pathname === "/";

  /** Legacy/bookmark links: open once then strip #contact so refresh does not reopen */
  const syncHashToPopup = useCallback(() => {
    if (location.hash !== "#contact") return;
    openContactPopup();
    navigate({ pathname: location.pathname, search: location.search, hash: "" }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, openContactPopup]);

  useEffect(() => {
    syncHashToPopup();
  }, [syncHashToPopup]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeContactPopup();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeContactPopup]);

  useEffect(() => {
    if (!open) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    lenis?.stop();
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      lenis?.start();
    };
  }, [open, lenis]);

  const fabContactId = isHome ? "contact" : undefined;
  const selectedService = CONTACT_SERVICES.find((service) => service.subject === contactDraft.subject) ?? null;

  return (
    <>
      <motion.button
        type="button"
        id={fabContactId}
        initial={false}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: "spring", stiffness: 420, damping: 28 }}
        style={{ bottom: `calc(${fabBottom} + ${footerOffset}px)` }}
        className={`contact-chat-fab fixed right-4 z-130 flex h-14 w-14 items-center justify-center rounded-2xl border text-lg font-bold tracking-tight shadow-xl backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
          open
            ? "border-gold/70 bg-[#170b08] text-gold shadow-[0_0_28px_rgba(254,74,43,0.28)] ring-2 ring-brand/20"
            : "border-gold/35 bg-bg-dark text-gold shadow-black/30 hover:border-brand/70 hover:text-brand"
        }`}
        aria-expanded={open}
        aria-controls={open ? "contact-chat-panel" : undefined}
        aria-label={open ? "Close contact form" : "Open contact form"}
        onClick={toggleContactPopup}
      >
        {open ? (
          <ChevronDown className="h-6 w-6 text-gold" strokeWidth={2.2} aria-hidden />
        ) : (
          <img src="/images/bonvi-logo-mini.svg" alt="" className="h-7 w-7 object-contain" aria-hidden="true" />
        )}
      </motion.button>

      {mounted
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <>
                  <motion.button
                    type="button"
                    key="contact-chat-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-105 bg-black/62 backdrop-blur-[3px]"
                    aria-label="Close contact"
                    onClick={closeContactPopup}
                  />
                  <motion.div
                    key="contact-chat-panel"
                    id="contact-chat-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 12, scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    style={{
                      bottom: panelBottom,
                      clipPath:
                        "polygon(18px 0, calc(100% - 18px) 0, 100% 18px, 100% calc(100% - 18px), calc(100% - 18px) 100%, 18px 100%, 0 calc(100% - 18px), 0 18px)",
                    }}
                    className="contact-chat-panel fixed left-2 right-2 z-110 flex max-h-[calc(100dvh-5.25rem)] flex-col overflow-hidden border border-gold/35 bg-[#090706] shadow-[0_28px_90px_rgba(0,0,0,0.58),0_0_42px_rgba(254,74,43,0.08)] sm:left-auto sm:right-4 sm:w-[min(calc(100vw-2rem),560px)] md:right-5 md:max-h-[min(78svh,35rem)] md:w-[min(calc(100vw-2.5rem),560px)] lg:w-145 xl:max-h-[min(78svh,36rem)] xl:w-150 2xl:max-h-[min(86svh,48rem)] 2xl:w-170 [@media_(min-width:1920px)_and_(min-height:1000px)]:max-h-[min(88svh,52rem)]"
                  >
                    <div className="relative flex shrink-0 items-start justify-between gap-3 border-b border-gold/20 px-3.5 pb-2 pt-3 md:px-4 md:pb-2.5 md:pt-3 2xl:px-6 2xl:pb-4 2xl:pt-6">
                      <div className="min-w-0 pr-2">
                        <h2 id={titleId} className="relative max-w-full font-display font-bold uppercase text-[#FE4A2B]">
                          Start Conversation
                        </h2>
                      </div>
                      <button
                        type="button"
                        className="contact-chat-close relative z-1 shrink-0 border border-gold/35 bg-black/50 text-gold transition-colors hover:border-brand/70 hover:bg-brand/10 hover:text-brand"
                        aria-label="Close"
                        onClick={closeContactPopup}
                      >
                        <X className="h-4.5 w-4.5 md:h-5 md:w-5" strokeWidth={2} />
                      </button>
                    </div>

                    <div
                      className="contact-chat-scroll relative min-h-0 flex-auto overflow-y-auto px-3.5 pb-3 pt-2 md:px-4 md:pb-3 md:pt-2 2xl:px-6 2xl:pb-5 2xl:pt-4"
                      data-lenis-prevent
                      data-lenis-prevent-wheel
                      data-lenis-prevent-touch
                      tabIndex={0}
                    >
                      <div className="pointer-events-none absolute inset-x-3.5 top-0 h-px bg-linear-to-r from-transparent via-brand/45 to-transparent md:inset-x-6" aria-hidden />
                      <ContactForm
                        className="contact-chat-form"
                        compact
                        embeddedPopup
                        motionDelay={0}
                        submitVariant="accent"
                        formInterlude={(
                          <ContactServicePicker
                            compact
                            selectedService={selectedService}
                            onChange={(service) => setContactDraft((previous) => ({
                              ...previous,
                              subject: service.subject,
                              message: service.message,
                            }))}
                          />
                        )}
                      />
                    </div>
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>,
            document.body
          )
        : null}
    </>
  );
}
